import React, {useEffect, useRef} from 'react';
import {Stack, styled} from '@mui/material';
import {designSystemToken} from '@lightricks/react-design-system';
import {calculateSmartWordTimings} from '@contentlab/components/video-display/captions-rendering/text-processing/WordTimingProcessor';
import {
  contentLabActions,
  DraftCaptionsProject,
} from '@contentlab/stores/contentLabStore';
import {scrollbarMixins} from '@contentlab/styles/mixins';
import {hasSrtItemBeenEdited, SrtItem} from '@contentlab/utils/srtUtils';
import SrtRow from '../srt-row/SrtRow';

const Container = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  spacing: '36px',
  background: designSystemToken('semantic.bg.secondary'),
  borderRadius: '8px',
  overflow: 'auto',
  gap: '28px',
  paddingInlineStart: '24px',
  paddingInlineEnd: '24px',
  ...scrollbarMixins.overrideScrollbarHover(),
}));

function CaptionsEditor({
  draftCaptionsProject,
  currentIndex,
  setCurrentIndex,
  onTranscriptionChange,
}: {
  draftCaptionsProject: DraftCaptionsProject;
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
  onTranscriptionChange?: () => void;
}) {
  const editorRef = useRef<HTMLDivElement>(null);

  const editRowWithSmartTiming = (newText: string, rowIndex: number) => {
    const currentRow = draftCaptionsProject.currentSrt[rowIndex];
    const originalRow = draftCaptionsProject.originalSrt[rowIndex];

    const smartWordTimings = calculateSmartWordTimings(newText, originalRow);

    const newSrt = [...draftCaptionsProject.currentSrt];
    newSrt[rowIndex] = {
      ...currentRow,
      currentText: newText,
      wordTimings: smartWordTimings,
    };
    contentLabActions.updateDraftCaptionsProjectSrt(newSrt);
    onTranscriptionChange?.();
  };

  const restoreOriginalRow = (rowIndex: number) => {
    const originalRow = draftCaptionsProject.originalSrt[rowIndex];

    const newSrt = [...draftCaptionsProject.currentSrt];
    newSrt[rowIndex] = {...originalRow};
    contentLabActions.updateDraftCaptionsProjectSrt(newSrt);
    onTranscriptionChange?.();
  };

  useEffect(() => {
    // Set the current index to the first item in the project if not already set
    const currentIndexId = draftCaptionsProject.currentSrt?.[0]?.id;

    if (currentIndexId && !currentIndex) {
      setCurrentIndex(currentIndexId);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element | null;

      const parentWithId = target?.closest('#captions-video-display');

      if (
        editorRef.current &&
        !editorRef.current.contains(target) &&
        !parentWithId
      ) {
        setCurrentIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setCurrentIndex]);

  return (
    <Container ref={editorRef}>
      <Stack gap="24px">
        {draftCaptionsProject.currentSrt.map((item: SrtItem, index: number) => {
          const originalItem = draftCaptionsProject.originalSrt[index];
          const isEdited = hasSrtItemBeenEdited(item, originalItem);
          return (
            <SrtRow
              key={item.id}
              item={item}
              isEditing={item.id === currentIndex}
              isEdited={isEdited}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              editRow={(newText) => editRowWithSmartTiming(newText, index)}
              restoreOriginal={() => restoreOriginalRow(index)}
            />
          );
        })}
      </Stack>
    </Container>
  );
}

export default CaptionsEditor;
